import { MPOST, POST, truncate } from 'common/helpers.ts';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';
import type { Leg } from 'types/Leg.ts';
import type { User } from 'types/User.ts';
import { Button } from 'ui/component/Button.tsx';
import { Modal } from 'ui/component/Modal.tsx';
import { Spinner } from 'ui/component/Spinner.tsx';
import { Select } from 'ui/control/Select.tsx';
import { getTranslatedError } from '#admin/helpers.ts';

type Props = {
  legs: Leg[];
  onSave?: (data: { supplier_id: string; leg_ids: string[] }) => void;
  onClose: () => void;
  isOpen: boolean;
};

export const BulkSupplierAssignmentDialog: React.FC<Props> = ({ legs, onSave, onClose, isOpen }) => {
  const { t } = useTranslation();

  const { data: suppliers, mutate: mutateSuppliers, isLoading } = useSWR<User[]>(['/user/list', { role: 'SUPPLIER', sort: [['name', 'asc']] }], POST);

  const validSuppliers = React.useMemo(() => {
    const options: Record<string, string> = {};
    if (!suppliers) return options;
    for (const supplier of suppliers) {
      const name = supplier.name.toUpperCase();
      const id = truncate(supplier.id, 8, '').toUpperCase();
      options[supplier.id] = `${name} - ${id}`;
    }
    return options;
  }, [suppliers]);

  const formMethods = useForm({
    mode: 'onTouched',
    criteriaMode: 'all',
    shouldUnregister: true,
    shouldUseNativeValidation: false,
    shouldFocusError: true,
    defaultValues: {
      supplierId: '',
    },
  });

  const { handleSubmit, formState, watch } = formMethods;
  const selectedSupplierId = watch('supplierId');
  const { trigger, isMutating, error } = useSWRMutation('/leg/bulk-assign-supplier', MPOST);

  const onSubmit = async ({ supplierId }) => {
    if (!suppliers) return;
    await trigger({ supplier_id: supplierId, leg_ids: legs.map((leg) => leg.id) });
    void mutateSuppliers();
    onSave?.({ supplier_id: supplierId, leg_ids: legs.map((leg) => leg.id) });
  };

  if (isLoading) return <Spinner centered={true} />;

  return (
    <Modal title={<h4>{t('dialogs.supplier.title')}</h4>} isOpen={isOpen} onClose={onClose}>
      <FormProvider {...formMethods}>
        <form className="flex w-max-[900px] flex-col gap-2.5" onSubmit={handleSubmit(onSubmit)}>
          <div className="flex flex-col gap-2">
            <Select name={'supplierId'} label={t('labels.supplierId')} options={validSuppliers} placeholder={t('options.none')} registerOptions={{ required: true }} />
          </div>

          <div className="mt-6 flex max-h-[400px] flex-col overflow-y-auto">
            <div className="grid h-[40px] grid-cols-4 items-center gap-2 border-grey-border border-b-1">
              <strong>Shipment ID</strong>
              <strong>From</strong>
              <strong></strong>
              <strong>To</strong>
            </div>

            {legs.map((leg) => (
              <div key={leg.id} className="grid h-[40px] grid-cols-4 items-center gap-2 border-grey-border border-b-1" data-shipment-id={leg.shipment_id} data-leg-id={leg.id}>
                <Link className="anchor anchor-novisit" to={`/shipment/${leg.shipment_logistics_ex_id}`} target="_blank">
                  {leg.shipment_logistics_ex_id}
                </Link>
                <span>{leg.origin_address_name}</span>
                <span className="text-center">{'→'}</span>
                <span>{leg.destination_address_name}</span>
              </div>
            ))}
          </div>

          <div className="mt-4 flex gap-5">
            <Button disabled={isMutating} className="blue-outlined" onClick={onClose}>
              {t('actions.back')}
            </Button>
            <Button type="submit" disabled={formState.isSubmitting || !selectedSupplierId} loading={formState.isSubmitting} className="blue flex items-center justify-center">
              {t('actions.save')}
            </Button>
          </div>
          {error && <span className="text-center text-red-500">{getTranslatedError(error, t) || t('dialogs.supplier.error')}</span>}
        </form>
      </FormProvider>
    </Modal>
  );
};
